import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, fetchTransactionsWithRetry } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export function useTransactionTable() {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const queryClient = useQueryClient();
  const channelRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [previousTransactions, setPreviousTransactions] = useState([]);
  const transactionCacheRef = useRef([]);
  const lastSuccessfulDataRef = useRef([]);
  const lastFetchTimeRef = useRef(0);
  const isUpdatingRef = useRef(false);
  
  // Debounced state update to prevent flickering
  const updateTransactionsWithDebounce = useCallback((newData) => {
    if (isUpdatingRef.current) return;
    
    // If there's no significant change, don't update UI
    if (newData.length === transactions.length && 
        JSON.stringify(newData.map(t => t.id).sort()) === 
        JSON.stringify(transactions.map(t => t.id).sort())) {
      return;
    }
    
    isUpdatingRef.current = true;
    
    // Store in cache
    transactionCacheRef.current = newData;
    lastSuccessfulDataRef.current = newData;
    
    // Smooth transition for UI updates
    setTimeout(() => {
      setTransactions(newData);
      isUpdatingRef.current = false;
    }, 50);
  }, [transactions]);

  // Memoized filter parsing
  const filters = {
    type: searchParams.get("type"),
    paymentMethod: searchParams.get("paymentMethod"),
    paymentStatus: searchParams.get("paymentStatus"),
    startDate: searchParams.get("startDate"),
    endDate: searchParams.get("endDate"),
    isFixedExpense: searchParams.get("isFixedExpense")
  };

  const fetchTransactions = useCallback(async (isForceRefresh = false) => {
    // Throttle requests to prevent excessive API calls
    const now = Date.now();
    if (!isForceRefresh && now - lastFetchTimeRef.current < 1000) {
      return;
    }
    lastFetchTimeRef.current = now;
    
    // For force refreshes, set loading state
    if (isForceRefresh) {
      setIsLoading(true);
    }
    
    // Only show loading on initial load, not on refreshes
    if (isInitialLoad && !isLoading) {
      setIsLoading(true);
    }
    
    setLoadError(false);
    
    try {
      console.log('Fetching transactions with filters:', filters);
      const { data, error } = await fetchTransactionsWithRetry(filters);
      
      if (error) throw error;
      
      if (data && Array.isArray(data)) {
        console.log(`Loaded ${data.length} transactions successfully`);
        setPreviousTransactions(transactionCacheRef.current);
        updateTransactionsWithDebounce(data);
        
        // Only invalidate other queries when we have successful data
        queryClient.invalidateQueries({ queryKey: ["financial-summary"] });
        queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
        queryClient.invalidateQueries({ queryKey: ["transactions-detail"] });
        
        // Initial load completed
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      } else {
        console.warn('No transaction data returned from API');
        if (transactionCacheRef.current.length > 0) {
          toast.warning("Usando dados em cache. Tente atualizar novamente em instantes.");
        } else {
          setTransactions([]);
        }
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setLoadError(true);
      
      if (transactionCacheRef.current.length > 0) {
        toast.error("Erro ao carregar dados. Exibindo dados em cache.");
      } else if (loadAttempts < 3) {
        setLoadAttempts(prev => prev + 1);
        setTimeout(() => fetchTransactions(), 2000);
        return;
      } else {
        setTransactions([]);
        toast.error("Falha ao carregar transações após várias tentativas.");
      }
    } finally {
      // Always set loading to false when done
      setIsLoading(false);
    }
  }, [filters, queryClient, loadAttempts, isInitialLoad, isLoading, updateTransactionsWithDebounce]);

  // Transaction edit handler
  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setEditDialogOpen(true);
  };

  // Transaction delete handler
  const handleDelete = (transaction) => {
    setSelectedTransaction(transaction);
    setDeleteDialogOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!selectedTransaction) return;

    try {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', selectedTransaction.id);

      if (error) throw error;

      toast.success("Transação excluída com sucesso");
      
      // Remove the deleted transaction from cache to prevent flickering
      const updatedTransactions = transactions.filter(t => t.id !== selectedTransaction.id);
      updateTransactionsWithDebounce(updatedTransactions);
      
      // Then fetch latest data
      fetchTransactions();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error("Não foi possível excluir a transação");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedTransaction(null);
    }
  };

  // Utility functions for UI display
  const getStatusColor = (status) => {
    switch (status) {
      case 'pago':
        return 'text-green-500';
      case 'parcial':
        return 'text-yellow-500';
      case 'pendente':
        return 'text-red-500';
      default:
        return 'text-gray-400';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'entrada':
        return 'text-green-500';
      case 'saida':
        return 'text-red-500';
      case 'despesas':
        return 'text-yellow-500';
      default:
        return 'text-gray-400';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'entrada':
        return 'Entrada';
      case 'saida':
        return 'Saída';
      case 'despesas':
        return 'Despesa Fixa';
      default:
        return type;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  // Initial data load on mount and when filters change
  useEffect(() => {
    fetchTransactions();
  }, [filters, fetchTransactions]);

  // Handle force refresh events
  useEffect(() => {
    const handleForceRefresh = () => {
      console.log('Force transaction refresh triggered');
      fetchTransactions(true);
    };
    
    window.addEventListener('forceTransactionRefresh', handleForceRefresh);
    
    return () => {
      window.removeEventListener('forceTransactionRefresh', handleForceRefresh);
    };
  }, [fetchTransactions]);

  // Realtime subscription for transactions
  useEffect(() => {
    const channelName = `transactions-${Date.now()}`;
    console.log(`Setting up real-time channel: ${channelName}`);
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'transactions' },
        (payload) => {
          console.log('Real-time transaction update received:', payload);
          // Use a buffered update approach to prevent flickering on rapid changes
          setTimeout(() => fetchTransactions(), 300);
        }
      )
      .subscribe((status) => {
        console.log('Transaction channel subscription status:', status);
      });
    
    channelRef.current = channel;
    
    return () => {
      console.log('Unsubscribing from transactions channel');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [fetchTransactions]);

  return {
    transactions,
    isLoading,
    isInitialLoad,
    loadError,
    deleteDialogOpen,
    setDeleteDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    selectedTransaction,
    handleEdit,
    handleDelete,
    confirmDelete,
    fetchTransactions,
    getStatusColor,
    getTypeColor,
    getTypeLabel,
    formatCurrency
  };
}
